/**
 * RetroGridBackground Component
 *
 * Remotion-native implementation of a retro/synthwave perspective grid.
 * Creates an 80s-style animated grid that recedes into the horizon.
 *
 * Features:
 * - 3D perspective transformation
 * - Animated grid lines moving toward viewer
 * - Customizable colors, cell size, and animation speed
 * - Gradient fade at horizon
 */

import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from 'remotion';

// Grid color presets
export const RETRO_GRID_PRESETS = {
  synthwave: {
    name: 'Synthwave',
    lineColor: '#ff00ff',
    backgroundColor: '#0a0a1a',
    horizonColor: '#ff6b00',
  },
  cyberpunk: {
    name: 'Cyberpunk',
    lineColor: '#00ffff',
    backgroundColor: '#0d0221',
    horizonColor: '#ff2a6d',
  },
  neon: {
    name: 'Neon',
    lineColor: '#39ff14',
    backgroundColor: '#0a0a0a',
    horizonColor: '#ff073a',
  },
  vaporwave: {
    name: 'Vaporwave',
    lineColor: '#ff71ce',
    backgroundColor: '#01cdfe',
    horizonColor: '#05ffa1',
  },
  midnight: {
    name: 'Midnight',
    lineColor: '#4a90d9',
    backgroundColor: '#0c1445',
    horizonColor: '#7b2d8e',
  },
  sunset: {
    name: 'Sunset',
    lineColor: '#ffb347',
    backgroundColor: '#1a0a2e',
    horizonColor: '#ff6b6b',
  },
  matrix: {
    name: 'Matrix',
    lineColor: '#00ff00',
    backgroundColor: '#000000',
    horizonColor: '#003300',
  },
  tron: {
    name: 'Tron',
    lineColor: '#00d4ff',
    backgroundColor: '#000814',
    horizonColor: '#ff6600',
  },
} as const;

export type RetroGridPreset = keyof typeof RETRO_GRID_PRESETS;

export interface RetroGridBackgroundProps {
  /** Preset color scheme */
  preset?: RetroGridPreset;
  /** Custom line color (overrides preset) */
  lineColor?: string;
  /** Custom background color (overrides preset) */
  backgroundColor?: string;
  /** Custom horizon glow color (overrides preset) */
  horizonColor?: string;
  /** Grid cell size in pixels */
  cellSize?: number;
  /** Perspective angle in degrees (0-80) */
  angle?: number;
  /** Line opacity (0-1) */
  lineOpacity?: number;
  /** Animation speed multiplier */
  speed?: number;
  /** Height of the horizon fade gradient (0-100%) */
  fadeHeight?: number;
  /** Enable horizon glow effect */
  showHorizonGlow?: boolean;
  /** Children to render on top */
  children?: React.ReactNode;
}

export const RetroGridBackground: React.FC<RetroGridBackgroundProps> = ({
  preset = 'synthwave',
  lineColor,
  backgroundColor,
  horizonColor,
  cellSize = 60,
  angle = 65,
  lineOpacity = 0.4,
  speed = 1,
  fadeHeight = 50,
  showHorizonGlow = true,
  children,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Get colors from preset or use custom
  // Fallback to 'synthwave' if preset doesn't exist (e.g., when AnimatedGradient palette is passed)
  const presetColors = RETRO_GRID_PRESETS[preset] || RETRO_GRID_PRESETS.synthwave;
  const finalLineColor = lineColor || presetColors.lineColor;
  const finalBgColor = backgroundColor || presetColors.backgroundColor;
  const finalHorizonColor = horizonColor || presetColors.horizonColor;

  // Calculate animation offset (grid moves toward viewer)
  // One complete cell cycle takes ~2 seconds at speed=1
  const cycleDuration = (2 * fps) / speed;
  const animationProgress = (frame % cycleDuration) / cycleDuration;
  const offsetY = animationProgress * cellSize;

  // Create gradient for grid lines (thicker lines for 3D perspective visibility)
  // Use backgroundImage (not shorthand 'background') to avoid resetting backgroundSize
  const gridGradient =
    `linear-gradient(to right, ${finalLineColor} 2px, transparent 2px), ` +
    `linear-gradient(to bottom, ${finalLineColor} 2px, transparent 2px)`;

  // Horizon glow gradient
  const horizonGlow = showHorizonGlow
    ? `radial-gradient(ellipse 80% 40% at 50% 0%, ${finalHorizonColor}40 0%, transparent 50%)`
    : 'none';

  // Fade gradient (makes grid disappear at horizon)
  const fadeGradient = `linear-gradient(to bottom,
    transparent 0%,
    transparent ${100 - fadeHeight}%,
    ${finalBgColor} 100%
  )`;

  return (
    <AbsoluteFill style={{ backgroundColor: finalBgColor, overflow: 'hidden' }}>
      {/* Horizon glow */}
      {showHorizonGlow && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '50%',
            background: horizonGlow,
            opacity: 0.8,
          }}
        />
      )}

      {/* Grid container with perspective */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: '-50%',
          right: '-50%',
          height: '70%',
          perspective: '400px',
          perspectiveOrigin: '50% 0%',
          overflow: 'hidden',
        }}
      >
        {/* Rotated grid plane */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            transformStyle: 'preserve-3d',
            transform: `rotateX(${angle}deg)`,
            transformOrigin: '50% 0%',
          }}
        >
          {/* Animated grid */}
          <div
            style={{
              position: 'absolute',
              top: `-${cellSize}px`,
              left: 0,
              right: 0,
              bottom: `-${cellSize * 2}px`,
              backgroundImage: gridGradient,
              backgroundSize: `${cellSize}px ${cellSize}px`,
              backgroundPosition: `0 ${offsetY}px`,
              opacity: lineOpacity,
            }}
          />
        </div>

        {/* Fade overlay */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: fadeGradient,
            pointerEvents: 'none',
          }}
        />
      </div>

      {/* Sun/horizon element (optional decorative) */}
      {showHorizonGlow && (
        <div
          style={{
            position: 'absolute',
            top: '25%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '300px',
            height: '150px',
            borderRadius: '50% 50% 0 0',
            background: `linear-gradient(to bottom, ${finalHorizonColor}, ${finalHorizonColor}80)`,
            opacity: interpolate(frame, [0, fps * 0.5], [0, 0.6], {
              extrapolateRight: 'clamp',
            }),
            filter: 'blur(20px)',
          }}
        />
      )}

      {/* Scanlines effect (subtle) */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `repeating-linear-gradient(
            0deg,
            transparent,
            transparent 2px,
            rgba(0, 0, 0, 0.1) 2px,
            rgba(0, 0, 0, 0.1) 4px
          )`,
          pointerEvents: 'none',
        }}
      />

      {/* Children (content on top) */}
      {children}
    </AbsoluteFill>
  );
};

export default RetroGridBackground;
